import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  companyService, directorService, shareholderService, relationshipService,
  auditLogService, documentService, snapshotService, reportService,
  entityService, orgChartService, companyReminderService
} from "../services/api";
import api from "../services/api";
import {
  ArrowLeft, Building2, Users, Briefcase, GitBranch, FileText, History,
  Network, Download, Edit, Plus, Calendar, AlertCircle, ZoomIn, ZoomOut, Maximize2, Image, FileDown, Bell, Trash2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { OrgChartDisplay, GroupTreeNode, OwnershipChart } from "../components/ChartComponents";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

export default function CompanyWorkspacePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("summary");
  
  // Per-company Report Date input (user enters manually)
  const [reportDateInput, setReportDateInput] = useState("");
  const [selectedReportDate, setSelectedReportDate] = useState(null);
  const [snapshotLoaded, setSnapshotLoaded] = useState(false);
  
  const [directors, setDirectors] = useState([]);
  const [shareholders, setShareholders] = useState([]);
  const [groupTree, setGroupTree] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [entities, setEntities] = useState([]);
  const [orgChart, setOrgChart] = useState(null);
  
  const [chartZoom, setChartZoom] = useState(100);
  const [chartType, setChartType] = useState("org");
  const [exporting, setExporting] = useState(false);
  const chartContainerRef = useRef(null);

  // Upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState("");
  const [uploading, setUploading] = useState(false);

  // Reminders state
  const [reminders, setReminders] = useState([]);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [reminderForm, setReminderForm] = useState({ title: "", description: "", start_date: "", renewal_frequency: "annual", notes: "" });

  // Country state for edit
  const [countries, setCountries] = useState([]);
  const [countrySearch, setCountrySearch] = useState("");

  useEffect(() => {
    import('../services/api').then(mod => {
      mod.default.get('/countries').then(res => setCountries(res.data.items || [])).catch(() => {});
    });
  }, []);
  const [showDirectorModal, setShowDirectorModal] = useState(false);
  const [showShareholderModal, setShowShareholderModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [directorForm, setDirectorForm] = useState({ entity_id: "", position: "Director", date_appointed: "" });
  const [shareholderForm, setShareholderForm] = useState({ entity_id: "", shares: 0, percentage: 0 });

  // Load company details on mount
  const [showEditModal, setShowEditModal] = useState(false);
  useEffect(() => {
    const loadCompany = async () => {
      try {
        const res = await companyService.getById(id);
        setCompany(res.data);
        setEditForm(res.data);
      } catch (error) {
        toast.error("Failed to load company");
        navigate("/companies");
      } finally {
        setLoading(false);
      }
    };
    loadCompany();
  }, [id, navigate]);

  // Load entities for modals
  useEffect(() => {
    entityService.getAll({ limit: 200 }).then(res => setEntities(res.data.items || [])).catch(() => {});
  }, []);

  // Load snapshot data when user clicks Load Snapshot
  const loadSnapshotData = useCallback(async () => {
    if (!selectedReportDate) return;
    try {
      const [snapRes, logsRes, treeRes, chartRes] = await Promise.all([
        snapshotService.get(id, selectedReportDate),
        auditLogService.getAll({ company_id: id, limit: 100 }),
        relationshipService.getTree(id, selectedReportDate).catch(() => ({ data: null })),
        orgChartService.get(id, selectedReportDate).catch(() => ({ data: null }))
      ]);
      
      setSnapshot(snapRes.data);
      setDirectors(snapRes.data.directors || []);
      setShareholders(snapRes.data.shareholders || []);
      setDocuments(snapRes.data.documents || []);
      
      // Use historical company name from snapshot
      if (snapRes.data.company) {
        setCompany(prev => ({...prev, ...snapRes.data.company}));
      }
      
      setAuditLogs(logsRes.data.items || []);
      setGroupTree(treeRes.data);
      setOrgChart(chartRes.data);
      setSnapshotLoaded(true);
    } catch (error) {
      toast.error("Failed to load snapshot data");
    }
  }, [id, selectedReportDate]);

  const handleLoadSnapshot = () => {
    if (!reportDateInput) {
      toast.error("Please enter a Report Date");
      return;
    }
    setSelectedReportDate(reportDateInput);
  };

  useEffect(() => {
    if (selectedReportDate) {
      loadSnapshotData();
    }
  }, [selectedReportDate, loadSnapshotData]);

  // Load reminders (not snapshot-dependent)
  const loadReminders = useCallback(async () => {
    try {
      const res = await companyReminderService.getAll(id);
      setReminders(res.data.items || []);
    } catch (error) {
      console.error("Failed to load reminders:", error);
    }
  }, [id]);

  useEffect(() => { loadReminders(); }, [loadReminders]);

  const handleSaveReminder = async (e) => {
    e.preventDefault();
    try {
      if (editingReminder) {
        await companyReminderService.update(editingReminder.id, reminderForm);
        toast.success("Reminder updated");
      } else {
        await companyReminderService.create({ ...reminderForm, company_id: id });
        toast.success("Reminder created");
      }
      setShowReminderModal(false);
      setEditingReminder(null);
      setReminderForm({ title: "", description: "", start_date: "", renewal_frequency: "annual", notes: "" });
      loadReminders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save reminder");
    }
  };

  const handleEditReminder = (r) => {
    setEditingReminder(r);
    setReminderForm({ title: r.title, description: r.description || "", start_date: r.start_date, renewal_frequency: r.renewal_frequency, notes: r.notes || "", status: r.status });
    setShowReminderModal(true);
  };

  const handleDeleteReminder = async (rid) => {
    if (!window.confirm("Delete this reminder?")) return;
    try {
      await companyReminderService.delete(rid);
      toast.success("Reminder deleted");
      loadReminders();
    } catch (error) {
      toast.error("Failed to delete reminder");
    }
  };

  const statusBadge = (status) => {
    const map = { overdue: "bg-red-100 text-red-700", due_soon: "bg-amber-100 text-amber-700", upcoming: "bg-blue-100 text-blue-700", ok: "bg-green-100 text-green-700" };
    const labels = { overdue: "Overdue", due_soon: "Due Soon", upcoming: "Upcoming", ok: "OK" };
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[status] || map.ok}`}>{labels[status] || status}</span>;
  };

  const freqLabel = (f) => ({ annual: "Annual", quarterly: "Quarterly", semi_annual: "Semi-Annual", every_3_years: "Every 3 Years", every_5_years: "Every 5 Years" }[f] || f);

  // Format current datetime for export stamps
  const formatGenerationDate = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
  };

  // Export chart as PNG
  const handleExportImage = async () => {
    if (!chartContainerRef.current) { toast.error("Chart not available for export"); return; }
    setExporting(true);
    try {
      const originalTransform = chartContainerRef.current.style.transform;
      chartContainerRef.current.style.transform = 'scale(1)';
      const canvas = await html2canvas(chartContainerRef.current, { backgroundColor: '#f8fafc', scale: 2, useCORS: true, logging: false });
      chartContainerRef.current.style.transform = originalTransform;

      const headerHeight = 80;
      const chartTitle = chartType === 'org' ? 'Organizational Chart' : chartType === 'group' ? 'Group Structure Chart' : 'Ownership Chart';
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = canvas.width;
      finalCanvas.height = canvas.height + headerHeight * 2;
      const ctx = finalCanvas.getContext('2d');

      ctx.fillStyle = '#f8fafc';
      ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
      ctx.fillStyle = '#1e3a8a';
      ctx.font = 'bold 28px sans-serif';
      ctx.fillText(`${company.name} - ${chartTitle}`, 28, 40);
      ctx.fillStyle = '#64748b';
      ctx.font = '20px sans-serif';
      ctx.fillText(`Report Date: ${selectedReportDate}`, 28, 75);
      ctx.fillText(`Generated On: ${formatGenerationDate()}`, 28, 105);
      ctx.drawImage(canvas, 0, headerHeight * 2);
      
      const link = document.createElement('a');
      const chartName = chartType === 'org' ? 'organizational' : chartType === 'group' ? 'group_structure' : 'ownership';
      link.download = `${company.company_code}_${chartName}_chart_${selectedReportDate}.png`;
      link.href = finalCanvas.toDataURL('image/png');
      link.click();
      toast.success("Chart exported as PNG");
    } catch (error) {
      toast.error("Failed to export chart");
    } finally {
      setExporting(false);
    }
  };

  // Export chart as PDF
  const handleExportPDF = async () => {
    if (!chartContainerRef.current) { toast.error("Chart not available for export"); return; }
    setExporting(true);
    try {
      const originalTransform = chartContainerRef.current.style.transform;
      chartContainerRef.current.style.transform = 'scale(1)';
      const canvas = await html2canvas(chartContainerRef.current, { backgroundColor: '#f8fafc', scale: 2, useCORS: true, logging: false });
      chartContainerRef.current.style.transform = originalTransform;

      const chartTitle = chartType === 'org' ? 'Organizational Chart' : chartType === 'group' ? 'Group Structure Chart' : 'Ownership Chart';
      const imgWidth = 280;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pdf = new jsPDF('l', 'mm', 'a4');

      pdf.setFontSize(16);
      pdf.text(`${company.name} - ${chartTitle}`, 14, 15);
      pdf.setFontSize(10);
      pdf.setTextColor(100);
      pdf.text(`Report Date: ${selectedReportDate}`, 14, 22);
      pdf.text(`Generated On: ${formatGenerationDate()}`, 14, 27);

      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', 5, 32, imgWidth, Math.min(imgHeight, 170));

      const chartName = chartType === 'org' ? 'organizational' : chartType === 'group' ? 'group_structure' : 'ownership';
      pdf.save(`${company.company_code}_${chartName}_chart_${selectedReportDate}.pdf`);
      toast.success("Chart exported as PDF");
    } catch (error) {
      toast.error("Failed to export chart as PDF");
    } finally {
      setExporting(false);
    }
  };

  // Generate org chart from directors
  const handleGenerateOrgChart = async () => {
    if (!selectedReportDate) { toast.error("Please enter a Report Date first"); return; }
    try {
      await orgChartService.generate(id, selectedReportDate);
      const chartRes = await orgChartService.get(id, selectedReportDate);
      setOrgChart(chartRes.data);
      toast.success("Org chart generated from directors");
    } catch (error) {
      toast.error("Failed to generate org chart");
    }
  };

  // Export report (PDF/DOCX/XLSX)
  const handleExportReport = async (format) => {
    if (!selectedReportDate) { toast.error("Please enter a Report Date first"); return; }
    try {
      const response = await reportService.generateCompanyProfile(id, format, selectedReportDate);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${company.company_code}_profile_${selectedReportDate}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Report exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(`Failed to export ${format.toUpperCase()} report`);
    }
  };

  // Document handlers
  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) { toast.error("Please select a file"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      const params = { company_id: id, description: uploadDescription };
      if (selectedReportDate) params.effective_date = selectedReportDate;
      await api.post('/documents/upload', formData, {
        params,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Document uploaded");
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadDescription("");
      loadSnapshotData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload");
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await documentService.download(doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await documentService.delete(docId);
      toast.success("Document deleted");
      loadSnapshotData();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  // Company update handler
  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    try {
      await companyService.update(id, editForm);
      const res = await companyService.getById(id);
      setCompany(res.data);
      setShowEditModal(false);
      toast.success("Company updated");
    } catch (error) {
      toast.error("Failed to update company");
    }
  };

  // Director add handler
  const handleAddDirector = async (e) => {
    e.preventDefault();
    if (!selectedReportDate) { toast.error("Please load a snapshot first"); return; }
    try {
      await directorService.create({
        company_id: id,
        entity_id: directorForm.entity_id,
        position: directorForm.position,
        date_appointed: directorForm.date_appointed,
        effective_date: selectedReportDate
      });
      setShowDirectorModal(false);
      setDirectorForm({ entity_id: "", position: "Director", date_appointed: "" });
      loadSnapshotData();
      toast.success("Director added");
    } catch (error) {
      toast.error("Failed to add director");
    }
  };

  // Shareholder add handler
  const handleAddShareholder = async (e) => {
    e.preventDefault();
    if (!selectedReportDate) { toast.error("Please load a snapshot first"); return; }
    try {
      await shareholderService.create({
        company_id: id,
        entity_id: shareholderForm.entity_id,
        shares: shareholderForm.shares,
        percentage: shareholderForm.percentage,
        effective_date: selectedReportDate
      });
      setShowShareholderModal(false);
      setShareholderForm({ entity_id: "", shares: 0, percentage: 0 });
      loadSnapshotData();
      toast.success("Shareholder added");
    } catch (error) {
      toast.error("Failed to add shareholder");
    }
  };

  // Empty state when no report date is entered
  const EmptySnapshotState = () => (
    <div className="text-center py-16" data-testid="empty-snapshot-state">
      <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
      <h3 className="text-lg font-medium text-slate-900 mb-2">No Report Date Entered</h3>
      <p className="text-slate-500 mb-6 max-w-md mx-auto">
        Please enter a Report Date to view the company snapshot.
      </p>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!company) return null;

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/companies")} data-testid="back-to-companies-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-slate-900" data-testid="company-name">{company.name}</h1>
          <p className="text-sm text-slate-500">{company.company_code} | {company.status}
            {company.last_changed_date && <span className="ml-2">| Last Changed: {company.last_changed_date}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setShowEditModal(true)} data-testid="edit-company-btn">
              <Edit className="w-4 h-4 mr-1" /> Edit
            </Button>
          )}
          <div className="relative group">
            <Button variant="outline" size="sm" data-testid="export-report-btn">
              <Download className="w-4 h-4 mr-1" /> Export
            </Button>
            <div className="absolute right-0 mt-1 w-40 bg-white border border-slate-200 rounded-md shadow-lg hidden group-hover:block z-10">
              <button className="w-full px-4 py-2 text-sm text-left hover:bg-slate-50" onClick={() => handleExportReport("pdf")} data-testid="export-pdf-btn">PDF Report</button>
              <button className="w-full px-4 py-2 text-sm text-left hover:bg-slate-50" onClick={() => handleExportReport("docx")} data-testid="export-docx-btn">Word Document</button>
              <button className="w-full px-4 py-2 text-sm text-left hover:bg-slate-50" onClick={() => handleExportReport("xlsx")} data-testid="export-xlsx-btn">Excel Spreadsheet</button>
            </div>
          </div>
        </div>
      </div>

      {/* Per-Company Report Date Input */}
      <div className="enterprise-card mb-6 p-4" data-testid="report-date-bar">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-sm font-medium text-slate-700">Report Date:</span>
          <Input
            type="date"
            value={reportDateInput}
            onChange={(e) => setReportDateInput(e.target.value)}
            className="form-input w-48"
            data-testid="report-date-input"
          />
          <Button onClick={handleLoadSnapshot} className="btn-primary" disabled={!reportDateInput} data-testid="load-snapshot-btn">
            Load Snapshot
          </Button>
          {snapshotLoaded && selectedReportDate && (
            <span className="text-sm text-blue-800 bg-blue-50 px-3 py-1 rounded" data-testid="snapshot-indicator">
              Viewing company structure as of {selectedReportDate}
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="enterprise-tabs mb-6">
          <TabsTrigger value="summary" data-testid="tab-summary"><Building2 className="w-4 h-4 mr-1" />Summary</TabsTrigger>
          <TabsTrigger value="directors" data-testid="tab-directors"><Users className="w-4 h-4 mr-1" />Directors</TabsTrigger>
          <TabsTrigger value="shareholders" data-testid="tab-shareholders"><Briefcase className="w-4 h-4 mr-1" />Shareholders</TabsTrigger>
          <TabsTrigger value="structure" data-testid="tab-structure"><GitBranch className="w-4 h-4 mr-1" />Structure</TabsTrigger>
          <TabsTrigger value="charts" data-testid="tab-charts"><Network className="w-4 h-4 mr-1" />Charts</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents"><FileText className="w-4 h-4 mr-1" />Documents</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="tab-reminders"><Bell className="w-4 h-4 mr-1" />Reminders</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><History className="w-4 h-4 mr-1" />Audit</TabsTrigger>
        </TabsList>

        {/* Summary Tab */}
        <TabsContent value="summary">
          <div className="enterprise-card">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-medium text-slate-900">Company Details</h3>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                ["Company Code", company.company_code],
                ["Registration No", company.registration_no],
                ["Short Name", company.short_name],
                ["Status", company.status],
                ["Industry", company.industry],
                ["Incorporation Date", company.incorporation_date],
                ["Place of Incorporation", company.place_of_incorporation],
                ["Address", company.address],
                ["Last Changed Date", company.last_changed_date || "N/A"]
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
                  <p className="text-sm text-slate-900 mt-1">{value || "N/A"}</p>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Directors Tab */}
        <TabsContent value="directors">
          {!snapshotLoaded ? <EmptySnapshotState /> : (
            <div className="enterprise-card">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-lg font-medium text-slate-900">Directors (as of {selectedReportDate})</h3>
                {isAdmin && <Button size="sm" className="btn-primary" onClick={() => setShowDirectorModal(true)} data-testid="add-director-btn"><Plus className="w-4 h-4 mr-1" />Add Director</Button>}
              </div>
              {directors.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="data-table"><thead><tr><th>Name</th><th>Position</th><th>Date Appointed</th><th>Date Ceased</th><th>Effective Date</th></tr></thead><tbody>
                    {directors.map((d) => (
                      <tr key={d.id} data-testid={`director-row-${d.id}`}>
                        <td className="font-medium">{d.entity_name}</td>
                        <td>{d.position}</td>
                        <td>{d.date_appointed}</td>
                        <td>{d.date_ceased || <span className="text-green-600">Active</span>}</td>
                        <td>{d.effective_date}</td>
                      </tr>
                    ))}
                  </tbody></table>
                </div>
              ) : (<div className="p-8 text-center"><Users className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No directors found for this date</p></div>)}
            </div>
          )}
        </TabsContent>

        {/* Shareholders Tab */}
        <TabsContent value="shareholders">
          {!snapshotLoaded ? <EmptySnapshotState /> : (
            <div className="enterprise-card">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-lg font-medium text-slate-900">Shareholders (as of {selectedReportDate})</h3>
                {isAdmin && <Button size="sm" className="btn-primary" onClick={() => setShowShareholderModal(true)} data-testid="add-shareholder-btn"><Plus className="w-4 h-4 mr-1" />Add Shareholder</Button>}
              </div>
              {shareholders.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="data-table"><thead><tr><th>Name</th><th>Type</th><th className="text-right">Shares</th><th className="text-right">Percentage</th><th>Effective Date</th></tr></thead><tbody>
                    {shareholders.map((s) => (
                      <tr key={s.id} data-testid={`shareholder-row-${s.id}`}>
                        <td className="font-medium">{s.entity_name}</td>
                        <td>{s.entity_type}</td>
                        <td className="text-right">{s.shares?.toLocaleString()}</td>
                        <td className="text-right">{s.percentage?.toFixed(2)}%</td>
                        <td>{s.effective_date}</td>
                      </tr>
                    ))}
                  </tbody></table>
                </div>
              ) : (<div className="p-8 text-center"><Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No shareholders found for this date</p></div>)}
            </div>
          )}
        </TabsContent>

        {/* Structure Tab */}
        <TabsContent value="structure">
          {!snapshotLoaded ? <EmptySnapshotState /> : (
            <div className="enterprise-card">
              <div className="px-6 py-4 border-b border-slate-200">
                <h3 className="text-lg font-medium text-slate-900">Group Structure (as of {selectedReportDate})</h3>
              </div>
              <div className="p-6">
                {groupTree ? (
                  <GroupTreeNode node={groupTree} currentCompanyId={id} onNavigate={(cid) => navigate(`/companies/${cid}`)} />
                ) : (
                  <div className="text-center py-8"><GitBranch className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No group structure found</p></div>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Charts Tab */}
        <TabsContent value="charts">
          {!snapshotLoaded ? <EmptySnapshotState /> : (
            <div className="enterprise-card">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-medium text-slate-900">Charts</h3>
                  <Select value={chartType} onValueChange={setChartType}>
                    <SelectTrigger className="w-48" data-testid="chart-type-selector"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="org">Organizational Chart</SelectItem>
                      <SelectItem value="group">Group Structure</SelectItem>
                      <SelectItem value="ownership">Ownership Chart</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setChartZoom(z => Math.max(25, z - 25))} data-testid="zoom-out-btn"><ZoomOut className="w-4 h-4" /></Button>
                  <span className="text-sm text-slate-600">{chartZoom}%</span>
                  <Button variant="outline" size="sm" onClick={() => setChartZoom(z => Math.min(200, z + 25))} data-testid="zoom-in-btn"><ZoomIn className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => setChartZoom(100)} data-testid="zoom-fit-btn"><Maximize2 className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={handleExportImage} disabled={exporting} data-testid="export-png-btn"><Image className="w-4 h-4 mr-1" />PNG</Button>
                  <Button variant="outline" size="sm" onClick={handleExportPDF} disabled={exporting} data-testid="export-chart-pdf-btn"><FileDown className="w-4 h-4 mr-1" />PDF</Button>
                  {chartType === "org" && <Button size="sm" className="btn-primary" onClick={handleGenerateOrgChart} data-testid="generate-org-chart-btn">Generate from Directors</Button>}
                </div>
              </div>
              <div className="p-6 overflow-auto" style={{ maxHeight: '600px' }}>
                <div ref={chartContainerRef} style={{ transform: `scale(${chartZoom / 100})`, transformOrigin: 'top left', minHeight: '300px' }}>
                  {chartType === "org" && (
                    orgChart?.tree?.length > 0 ? <OrgChartDisplay tree={orgChart.tree} /> :
                    <div className="text-center py-8"><Network className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No org chart data. Click "Generate from Directors" to create.</p></div>
                  )}
                  {chartType === "group" && (
                    groupTree ? <GroupTreeNode node={groupTree} currentCompanyId={id} onNavigate={(cid) => navigate(`/companies/${cid}`)} /> :
                    <div className="text-center py-8"><GitBranch className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No group structure data</p></div>
                  )}
                  {chartType === "ownership" && (
                    <OwnershipChart shareholders={shareholders} />
                  )}
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          {!snapshotLoaded ? <EmptySnapshotState /> : (
            <div className="enterprise-card">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-lg font-medium text-slate-900">Documents</h3>
                <Button size="sm" className="btn-primary" onClick={() => setShowUploadModal(true)} data-testid="upload-document-btn"><Plus className="w-4 h-4 mr-1" />Upload Document</Button>
              </div>
              {documents.length > 0 ? (
                <div className="divide-y divide-slate-200">
                  {documents.map((doc) => (
                    <div key={doc.id} className="px-6 py-4 flex items-center gap-4" data-testid={`document-row-${doc.id}`}>
                      <FileText className="w-8 h-8 text-slate-400" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900">{doc.file_name}</p>
                        <p className="text-xs text-slate-500">
                          {doc.file_type} {doc.file_size ? `| ${(doc.file_size / 1024).toFixed(1)} KB` : ''} | Uploaded {doc.uploaded_at?.split('T')[0]}
                          {doc.description && <span className="ml-2">- {doc.description}</span>}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDownloadDocument(doc)} data-testid={`download-doc-${doc.id}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                      {isAdmin && (
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteDocument(doc.id)} data-testid={`delete-doc-${doc.id}`}>
                          <AlertCircle className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (<div className="p-8 text-center"><FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No documents uploaded</p></div>)}
            </div>
          )}
        </TabsContent>

        {/* Reminders Tab */}
        <TabsContent value="reminders">
          <div className="enterprise-card">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-slate-900">Renewal Reminders</h3>
              <Button size="sm" className="btn-primary" onClick={() => { setEditingReminder(null); setReminderForm({ title: "", description: "", start_date: "", renewal_frequency: "annual", notes: "" }); setShowReminderModal(true); }} data-testid="add-reminder-btn">
                <Plus className="w-4 h-4 mr-1" />Add Reminder
              </Button>
            </div>
            {reminders.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Frequency</th>
                      <th>Start Date</th>
                      <th>Next Due</th>
                      <th>Status</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reminders.map((r) => (
                      <tr key={r.id} data-testid={`reminder-row-${r.id}`}>
                        <td>
                          <div>
                            <span className="font-medium text-slate-900">{r.title}</span>
                            {r.description && <p className="text-xs text-slate-500 mt-0.5">{r.description}</p>}
                          </div>
                        </td>
                        <td>{freqLabel(r.renewal_frequency)}</td>
                        <td>{r.start_date}</td>
                        <td className="font-medium">{r.next_due_date}</td>
                        <td>{statusBadge(r.computed_status)}</td>
                        <td className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleEditReminder(r)} data-testid={`edit-reminder-${r.id}`}><Edit className="w-4 h-4" /></Button>
                          {isAdmin && <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteReminder(r.id)} data-testid={`delete-reminder-${r.id}`}><Trash2 className="w-4 h-4" /></Button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No reminders set</p>
                <p className="text-sm text-slate-400 mt-1">Add renewal reminders to track important deadlines</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Audit Tab */}
        <TabsContent value="audit">
          <div className="enterprise-card">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-medium text-slate-900">Audit Log</h3>
            </div>
            {auditLogs.length > 0 ? (
              <div className="divide-y divide-slate-200">
                {auditLogs.map((log) => (
                  <div key={log.id} className="px-6 py-3" data-testid={`audit-row-${log.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-slate-900 capitalize">{log.action}</span>
                        <span className="text-sm text-slate-500 ml-2">{log.entity_type}</span>
                      </div>
                      <span className="text-xs text-slate-400">{log.changed_at}</span>
                    </div>
                    {log.changed_by_name && <p className="text-xs text-slate-500 mt-1">by {log.changed_by_name}</p>}
                  </div>
                ))}
              </div>
            ) : (<div className="p-8 text-center"><History className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No audit logs</p></div>)}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Company Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Company</DialogTitle><DialogDescription>Update company details</DialogDescription></DialogHeader>
          <form onSubmit={handleUpdateCompany}>
            <div className="space-y-4 py-4">
              {["name", "short_name", "registration_no", "status", "industry", "address"].map(field => (
                <div key={field}>
                  <Label className="form-label capitalize">{field.replace(/_/g, ' ')}</Label>
                  <Input value={editForm[field] || ""} onChange={(e) => setEditForm({...editForm, [field]: e.target.value})} className="form-input" data-testid={`edit-${field}-input`} />
                </div>
              ))}
              <div>
                <Label className="form-label">Place of Incorporation</Label>
                <Select value={editForm.place_of_incorporation_code || ""} onValueChange={(v) => {
                  const c = countries.find(c => c.code === v);
                  setEditForm({...editForm, place_of_incorporation_code: v, place_of_incorporation: c?.name || v});
                }}>
                  <SelectTrigger data-testid="edit-place-select"><SelectValue placeholder="Select country" /></SelectTrigger>
                  <SelectContent>
                    <div className="px-2 py-1">
                      <Input placeholder="Search..." value={countrySearch} onChange={(e) => setCountrySearch(e.target.value)} className="form-input h-8 text-sm" />
                    </div>
                    {countries.filter(c => !countrySearch || c.name.toLowerCase().includes(countrySearch.toLowerCase())).slice(0, 50).map(c => (
                      <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary" data-testid="save-company-btn">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Director Modal */}
      <Dialog open={showDirectorModal} onOpenChange={setShowDirectorModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Director</DialogTitle><DialogDescription>Add a new director to this company</DialogDescription></DialogHeader>
          <form onSubmit={handleAddDirector}>
            <div className="space-y-4 py-4">
              <div>
                <Label className="form-label">Person *</Label>
                <Select value={directorForm.entity_id} onValueChange={(v) => setDirectorForm({...directorForm, entity_id: v})}>
                  <SelectTrigger data-testid="director-entity-select"><SelectValue placeholder="Select person" /></SelectTrigger>
                  <SelectContent>{entities.filter(e => e.entity_type === "person").map(e => (<SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Position</Label>
                <Select value={directorForm.position} onValueChange={(v) => setDirectorForm({...directorForm, position: v})}>
                  <SelectTrigger data-testid="director-position-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Chairman", "Managing Director", "Executive Director", "Director", "Non-Executive Director"].map(p => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Date Appointed *</Label>
                <Input type="date" value={directorForm.date_appointed} onChange={(e) => setDirectorForm({...directorForm, date_appointed: e.target.value})} className="form-input" data-testid="director-date-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDirectorModal(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary" data-testid="save-director-btn">Add Director</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Shareholder Modal */}
      <Dialog open={showShareholderModal} onOpenChange={setShowShareholderModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Shareholder</DialogTitle><DialogDescription>Add a new shareholder to this company</DialogDescription></DialogHeader>
          <form onSubmit={handleAddShareholder}>
            <div className="space-y-4 py-4">
              <div>
                <Label className="form-label">Entity *</Label>
                <Select value={shareholderForm.entity_id} onValueChange={(v) => setShareholderForm({...shareholderForm, entity_id: v})}>
                  <SelectTrigger data-testid="shareholder-entity-select"><SelectValue placeholder="Select entity" /></SelectTrigger>
                  <SelectContent>{entities.map(e => (<SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Shares *</Label>
                <Input type="number" value={shareholderForm.shares} onChange={(e) => setShareholderForm({...shareholderForm, shares: parseInt(e.target.value) || 0})} className="form-input" data-testid="shareholder-shares-input" />
              </div>
              <div>
                <Label className="form-label">Percentage *</Label>
                <Input type="number" step="0.01" value={shareholderForm.percentage} onChange={(e) => setShareholderForm({...shareholderForm, percentage: parseFloat(e.target.value) || 0})} className="form-input" data-testid="shareholder-percentage-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowShareholderModal(false)}>Cancel</Button>
              <Button type="submit" className="btn-primary" data-testid="save-shareholder-btn">Add Shareholder</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Document Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Document</DialogTitle><DialogDescription>Upload a file to this company</DialogDescription></DialogHeader>
          <form onSubmit={handleFileUpload}>
            <div className="space-y-4 py-4">
              <div>
                <Label className="form-label">File *</Label>
                <Input type="file" onChange={(e) => setUploadFile(e.target.files[0])} className="form-input" data-testid="file-input" />
              </div>
              <div>
                <Label className="form-label">Description</Label>
                <Input value={uploadDescription} onChange={(e) => setUploadDescription(e.target.value)} placeholder="Optional description" className="form-input" data-testid="upload-description-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowUploadModal(false); setUploadFile(null); setUploadDescription(""); }}>Cancel</Button>
              <Button type="submit" className="btn-primary" disabled={uploading || !uploadFile} data-testid="upload-submit-btn">{uploading ? "Uploading..." : "Upload"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Reminder Modal */}
      <Dialog open={showReminderModal} onOpenChange={setShowReminderModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingReminder ? "Edit Reminder" : "Add Reminder"}</DialogTitle>
            <DialogDescription>Set a renewal reminder for this company. The next due date is auto-calculated.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveReminder}>
            <div className="space-y-4 py-4">
              <div>
                <Label className="form-label">Title *</Label>
                <Input value={reminderForm.title} onChange={(e) => setReminderForm({...reminderForm, title: e.target.value})} placeholder="e.g. Annual Return Filing" className="form-input" required data-testid="reminder-title-input" />
              </div>
              <div>
                <Label className="form-label">Description</Label>
                <Input value={reminderForm.description} onChange={(e) => setReminderForm({...reminderForm, description: e.target.value})} placeholder="Optional description" className="form-input" data-testid="reminder-description-input" />
              </div>
              <div>
                <Label className="form-label">Start Date *</Label>
                <Input type="date" value={reminderForm.start_date} onChange={(e) => setReminderForm({...reminderForm, start_date: e.target.value})} className="form-input" required data-testid="reminder-start-date-input" />
              </div>
              <div>
                <Label className="form-label">Renewal Frequency *</Label>
                <Select value={reminderForm.renewal_frequency} onValueChange={(v) => setReminderForm({...reminderForm, renewal_frequency: v})}>
                  <SelectTrigger data-testid="reminder-frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="semi_annual">Semi-Annual</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                    <SelectItem value="every_3_years">Every 3 Years</SelectItem>
                    <SelectItem value="every_5_years">Every 5 Years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editingReminder && (
                <div>
                  <Label className="form-label">Status</Label>
                  <Select value={reminderForm.status || "active"} onValueChange={(v) => setReminderForm({...reminderForm, status: v})}>
                    <SelectTrigger data-testid="reminder-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="form-label">Notes</Label>
                <Input value={reminderForm.notes} onChange={(e) => setReminderForm({...reminderForm, notes: e.target.value})} placeholder="Additional notes" className="form-input" data-testid="reminder-notes-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowReminderModal(false); setEditingReminder(null); }}>Cancel</Button>
              <Button type="submit" className="btn-primary" data-testid="save-reminder-btn">{editingReminder ? "Update Reminder" : "Add Reminder"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
